import React, { useState, useRef, useEffect } from 'react';
import { bookingService } from '../../services/bookingService';

interface FaisOverlayProps {
  content: string;
  /** Booking ID for the acknowledge API call. Pass null for guest users (UI-only acknowledgement). */
  bookingId: string | null;
  onAcknowledged: () => void;
}

export const FaisOverlay: React.FC<FaisOverlayProps> = ({ content, bookingId, onAcknowledged }) => {
  const [hasScrolled, setHasScrolled] = useState(false);
  const [loading, setLoading] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  // If content fits without scrolling, enable acknowledge immediately
  useEffect(() => {
    const el = scrollRef.current;
    if (el && el.scrollHeight <= el.clientHeight) {
      setHasScrolled(true);
    }
  }, [content]);

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - scrollTop - clientHeight < 10) {
      setHasScrolled(true);
    }
  };

  const handleAcknowledge = async () => {
    setLoading(true);
    setApiError(null);
    try {
      if (bookingId) {
        await bookingService.acknowledgeFais(bookingId);
      }
    } catch (err) {
      console.warn('FAIS acknowledge API error:', err);
      setLoading(false);
      setApiError('Could not record acknowledgement. Please try again.');
      return;
    }
    onAcknowledged();
  };

  return (
    <div
      className="fixed inset-0 bg-black/90 z-[9999] flex items-center justify-center p-4"
      data-testid="fais-overlay"
    >
      <div className="bg-gray-900 border border-amber-500 rounded-lg max-w-2xl w-full max-h-[80vh] flex flex-col">
        <div className="p-6 border-b border-gray-700">
          <h2 className="text-amber-400 font-bold text-lg">Regulatory Disclosure — FAIS</h2>
        </div>
        <div
          ref={scrollRef}
          className="overflow-y-auto p-6 flex-1 text-sm text-gray-300 whitespace-pre-wrap"
          onScroll={handleScroll}
          data-testid="fais-scroll-body"
        >
          {content}
        </div>
        <div className="p-6 border-t border-gray-700">
          <button
            disabled={!hasScrolled || loading}
            onClick={handleAcknowledge}
            className="w-full py-3 bg-amber-500 text-black font-bold rounded disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid="fais-acknowledge-btn"
          >
            {loading ? 'Processing...' : 'I Acknowledge'}
          </button>
          {!hasScrolled && (
            <p className="text-xs text-gray-500 text-center mt-2">
              Please scroll to the bottom to acknowledge
            </p>
          )}
          {apiError && (
            <p className="text-xs text-red-400 text-center mt-2" data-testid="fais-api-error">
              {apiError}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default FaisOverlay;
